import {Component, EventEmitter, Injector, Input, Output} from '@angular/core';
import {ClientConfigurationScheme, ComponentConfiguration, ComponentInit, InitialConfig} from '@twpub/core/utils';
import {
  ComponentFieldModel,
  ConfigurableComponent,
  DictionarySelector,
  FieldProps,
  SelectItem
} from '@twpub/core/models';
import {ComponentCategory, ControlType} from '@twpub/core/enums';
import {BaseConfigurableComponent} from '@twpub/shared/components/base';
import {DropdownComponent} from '../../utils/dropdown-component';
import _ from 'lodash';
import {DictionaryService} from '@twpub/core/services';
import {map} from 'rxjs/operators';

import {MultiSelectComponent} from '../multi-select/multi-select.component';

const defaultDictionaryOptions = (injector: Injector) => {
  const dictService = injector.get(DictionaryService);
  return dictService.getDictionaries(true).pipe(
    map(dicts => (dicts || []).map(d => ({value: d.id.toString(), label: d.name}))
    ));
};

@Component({
  selector: 'pub-dictionary-dropdown',
  templateUrl: './dictionary-dropdown.component.html',
  styleUrls: ['./dictionary-dropdown.component.scss'],
  standalone: true,
  imports: [MultiSelectComponent]
})
export class DictionaryDropdownComponent extends BaseConfigurableComponent implements DictionarySelector {
  static readonly compName = 'DictionaryDropdownComponent';
  static readonly MULTIPLE_SELECT = 'multipleSelect';
  static readonly DEFAULT_VALUE = 'defaultValue';

  @Input() items: SelectItem[] = [];
  @Input() selectedValues?: string[] = [];

  @Output() selectValues = new EventEmitter<number[]>()

  multipleSelect: boolean = false;
  allowAllSelection: boolean = false;
  allSelectedLabel?: string;

  selectedDictionaryName?: string

  static Init = class extends ComponentInit {
    readonly scheme = ClientConfigurationScheme.DEFAULT;
    override props: FieldProps[] = [this.LABEL_FIELD, this.VISIBLE_FIELD,
      {
        key: DropdownComponent.MULTIPLE_SELECT,
        label: 'Multiple select',
        controlType: ControlType.Boolean,
        description: 'Allow multiple selections in dropdown'
      },
      {
        key: DropdownComponent.DEFAULT_VALUE,
        label: 'Default dictionary',
        controlType: ControlType.Dropdown,
        options: defaultDictionaryOptions
      }
    ];

    constructor() {
      super(DictionaryDropdownComponent, DictionaryDropdownComponent.compName, 'Dropdown List', [ComponentCategory.DictionarySelector]);
    }

    createConfig(initialConfig: InitialConfig = undefined) {
      return new ComponentConfiguration<DictionarySelector>(DictionaryDropdownComponent, this.name, initialConfig);
    }

    override getFields(compConfig: ComponentConfiguration<ConfigurableComponent>): ComponentFieldModel<any>[] {
      const fields = super.getFields(compConfig);
      return DropdownComponent.getFields(compConfig, fields);
    }
  }

  override doUpdate(): void {
    super.doUpdate();
    this.multipleSelect = !!this.config?.getBoolean(DropdownComponent.MULTIPLE_SELECT);
    this.allowAllSelection = this.multipleSelect;
    this.allSelectedLabel = this.allowAllSelection ? $localize`:@@dictionary.allSelected:All Dictionaries` : undefined;
    this.updateDictName(this.selectedValues);

    let newSelectedValues = this.filterExisting(this.selectedValues);
    if (!newSelectedValues?.length) {
      newSelectedValues = this.setDefaultDictionaries();
    }
    if (!newSelectedValues?.length) {
      newSelectedValues = !this.selectedValues?.length && this.allowAllSelection ? [] : this.selectFirstAvailable();
    }
    if (!_.isEqual(newSelectedValues, this.selectedValues)) {
      this.selectedValues = newSelectedValues;
      this.selectDictionaries(newSelectedValues);
    }
  }

  private selectFirstAvailable = () => {
    return this.items?.[0] ? [this.items[0].value] : [];
  }

  private setDefaultDictionaries = () => {
    const defaultValues = this.filterExisting(this.config?.getStringArray(DropdownComponent.DEFAULT_VALUE, []));
    return this.multipleSelect ? defaultValues : defaultValues?.slice(0, 1);
  }

  private filterExisting = (itemIds: string[] | undefined) => {
    if (!itemIds?.length) return undefined;
    const validItems = itemIds.filter(id => this.items?.some(l => l.value === id));
    return validItems.length < itemIds.length ? validItems : itemIds;
  };

  onSelectValues(selectedValues: string[]) {
    this.selectDictionaries(selectedValues);
  }

  private selectDictionaries = (selectedValues: string[]) => {
    this.updateDictName(selectedValues);
    const values = selectedValues.map(id => parseInt(id));
    this.selectValues.emit(values);
  }

  private updateDictName(selectedIds?: string[]) {
    const selectedItems = this.items?.filter(item => selectedIds?.find(id => id === item.value));
    if (selectedItems.length) {
      this.selectedDictionaryName = selectedItems.map(item => item.name).join(', ');
    } else {
      this.selectedDictionaryName = undefined;
    }
  }
}
